// components/SampleApprovalModal.jsx
//
// The review surface for a sample request that arrived through the PUBLIC form
// (migration 0097 / backlog item 124).
//
// This modal is the whole point of the gate. The control that item 28 relies on
// — "the submitter has no field that influences where the goods ship" — does not
// exist for samples: the ship-to is free text typed by an anonymous caller. What
// replaces it is a human reading that address before anything leaves the
// building. So this modal shows the ENTIRE submission — every caller-supplied
// field, verbatim, in one place — above the Approve button. An approval control
// that doesn't show what is being approved is theatre.
//
// Rendering is React text nodes only (no dangerouslySetInnerHTML anywhere in
// this module), so a script tag typed into the address field is displayed, not
// executed.
import React, { useState } from "react";
import { approveSampleRequest, rejectSampleRequest } from "../services/posService";
import { APPROVAL, approvalStatusOf, REJECT_REASON_MAX } from "../utils/approval.js";
import "./SampleApprovalModal.css";

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(String(dateStr).slice(0, 10) + "T00:00:00");
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function Field({ label, value, wide = false, missing = "— not supplied —" }) {
  const empty = value === null || value === undefined || String(value).trim() === "";
  return (
    <div className={`sam-field${wide ? " sam-field-wide" : ""}`}>
      <div className="sam-field-label">{label}</div>
      <div className={`sam-field-value${empty ? " sam-field-empty" : ""}`}>
        {empty ? missing : String(value)}
      </div>
    </div>
  );
}

export default function SampleApprovalModal({ request, onClose, onRuled }) {
  const [mode, setMode] = useState(null); // null | 'reject'
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const status = approvalStatusOf(request);
  const isPending = status === APPROVAL.PENDING;

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await fn();
      onRuled?.(updated);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = () => run(() => approveSampleRequest(request.id));
  const handleReject = () => {
    if (!reason.trim()) { setError("Give a reason for rejecting this request."); return; }
    run(() => rejectSampleRequest(request.id, reason));
  };

  return (
    <div className="sam-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sam-modal" role="dialog" aria-modal="true" aria-label="Review sample request">

        <div className="sam-header">
          <div className="sam-title">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" />
            </svg>
            Review sample request
          </div>
          <button className="sam-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="sam-banner">
          <strong>Submitted through your public sample form.</strong> Nobody typed this address into
          Brand Command — the requester did. Check it, and the quantities, before you approve.
          Nothing can be marked Shipped until you do.
        </div>

        <div className="sam-body">

          <div className="sam-section">
            <div className="sam-section-title">Who asked for it</div>
            <div className="sam-grid">
              <Field label="Requester" value={request.submitter} />
              <Field label="Requester email" value={request.submitterEmail} />
            </div>
          </div>

          <div className="sam-section sam-section-alert">
            <div className="sam-section-title">Where it would ship — free text, as typed</div>
            <div className="sam-grid">
              <Field label="Company" value={request.shipToCompany} />
              <Field label="State" value={request.state} />
              <Field label="Street address" value={request.address} wide />
              <Field label="Delivery contact" value={request.contactName} />
              <Field label="Contact phone" value={request.contactPhone} />
              <Field label="Contact email" value={request.contactEmail} wide />
            </div>
          </div>

          <div className="sam-section">
            <div className="sam-section-title">What would ship</div>
            <div className="sam-grid">
              <Field label="Products and quantities" value={request.product} wide />
              {request.quantity ? <Field label="Quantity" value={request.quantity} /> : null}
              <Field label="Requested date" value={fmtDate(request.orderDate)} />
              <Field label="Requested ship date" value={fmtDate(request.requestedShipDate)} />
              <Field label="Notes from the requester" value={request.notes} wide missing="— none —" />
            </div>
          </div>

          {!isPending && (
            <div className="sam-section">
              <div className="sam-section-title">Already ruled on</div>
              <div className="sam-grid">
                <Field label="Decision" value={status === APPROVAL.APPROVED ? "Approved" : "Rejected"} />
                {status === APPROVAL.REJECTED
                  ? <Field label="Reason" value={request.rejectedReason} wide />
                  : null}
              </div>
            </div>
          )}

          {mode === "reject" && (
            <div className="sam-section sam-section-reject">
              <label className="sam-section-title" htmlFor="sam-reason">
                Why are you rejecting this? (stored on the request)
              </label>
              <textarea
                id="sam-reason"
                className="sam-reason"
                rows={3}
                maxLength={REJECT_REASON_MAX}
                autoFocus
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g. address doesn't match the account, duplicate of an earlier request, quantities look wrong"
              />
            </div>
          )}

          {error && <div className="sam-error" role="alert">{error}</div>}
        </div>

        <div className="sam-footer">
          <button className="sam-cancel-btn" onClick={onClose} disabled={busy}>Close</button>
          {isPending && (mode === "reject" ? (
            <>
              <button className="sam-cancel-btn" onClick={() => { setMode(null); setError(null); }} disabled={busy}>
                Back
              </button>
              <button className="sam-reject-btn" onClick={handleReject} disabled={busy || !reason.trim()}>
                {busy ? "Rejecting…" : "Reject request"}
              </button>
            </>
          ) : (
            <>
              <button className="sam-reject-btn" onClick={() => { setMode("reject"); setError(null); }} disabled={busy}>
                Reject
              </button>
              <button className="sam-approve-btn" onClick={handleApprove} disabled={busy}>
                {busy ? "Approving…" : "Approve — allow this to ship"}
              </button>
            </>
          ))}
        </div>

      </div>
    </div>
  );
}
